import { useCallback } from "react";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Enduser, Form, FormResponseValue, FormResponseValueAnswer } from "@tellescope/types-models";
import {
  applyFieldResponseMetadata,
  convertFormFieldType,
  convertStoredResponseToLocal,
  deriveComputedValueKey,
  dropStaleChoiceSelections,
  isAnswerCompatibleWithField,
  isValueEmpty,
  resolvePrepopulatedValue,
} from "../../utils";
import { TellescopeFieldResponse } from "../../types";
import { useSeedEmptyResponse } from "./use-seed-empty-response";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";

export interface UseResolveCurrentResponsesArgs {
  publicIdentifier: string | undefined;
}

export interface ResolveCurrentResponsesArgs {
  fieldsArr: TellescopeModelFromName<"form_fields">[] | undefined;
  unsavedResponses: LocalTellescopeFormResponseMap;
  responsesMap: Record<string, FormResponseValue> | undefined;
  existingResponses: FormResponseValue[] | undefined;
  enduserForPrepopulation: TellescopeModelFromName<"endusers"> | Enduser | undefined;
  form: Form | undefined;
}

// A locked field stays locked when its answer is resumed from a draft or an earlier submission, not
// only on the pass that prepopulates it. Legacy re-evaluated this at render from the field config
// plus a non-empty value (forms.tsx:255), so reopening a form never handed back an editable input.
const isLockedWhenPrepopulated = (
  field: TellescopeModelFromName<"form_fields">,
  answer: FormResponseValueAnswer
): boolean => !!field.disabledWhenPrepopulated && !!field.prepopulateFromFields && !isValueEmpty(answer.value);

const hydrateStoredResponse = (
  stored: FormResponseValue,
  field: TellescopeModelFromName<"form_fields">
): TellescopeFieldResponse => {
  const hydrated = convertStoredResponseToLocal(stored);
  return {
    ...hydrated,
    computedValueKey: hydrated.computedValueKey ?? deriveComputedValueKey(field.intakeField, hydrated.answer),
    ...(isLockedWhenPrepopulated(field, hydrated.answer) ? { disabled: true } : {}),
  };
};

/**
 * Returns a resolver for the effective answer of every field, in precedence order: an in-progress
 * edit wins over a saved response, which wins over a response passed in from a previous submission,
 * which wins over a value prepopulated from the enduser record, which wins over the field's seeded default.
 */
export const useResolveCurrentResponses = ({ publicIdentifier }: UseResolveCurrentResponsesArgs) => {
  const seedEmptyResponse = useSeedEmptyResponse({ publicIdentifier });

  return useCallback(
    ({
      fieldsArr,
      unsavedResponses,
      responsesMap,
      existingResponses,
      enduserForPrepopulation,
      form,
    }: ResolveCurrentResponsesArgs): LocalTellescopeFormResponseMap => {
      const resolved = fieldsArr?.reduce<LocalTellescopeFormResponseMap>((acc, field) => {
        const unsavedResponse = unsavedResponses[field.id];
        if (unsavedResponse) {
          acc[field.id] = unsavedResponse;
          return acc;
        }

        const savedResponse = responsesMap?.[field.id];
        if (savedResponse) {
          if (convertFormFieldType(savedResponse.answer.type) === null) return acc;
          if (!isAnswerCompatibleWithField(savedResponse.answer, field.type)) return acc;
          acc[field.id] = hydrateStoredResponse(savedResponse, field);
          return acc;
        }

        if (existingResponses) {
          const existing = existingResponses.find((r) => r.fieldId === field.id);
          if (existing) {
            if (
              convertFormFieldType(existing.answer.type) !== null &&
              isAnswerCompatibleWithField(existing.answer, field.type)
            ) {
              acc[field.id] = hydrateStoredResponse(existing, field);
              return acc;
            }
          }
        }

        if (convertFormFieldType(field.type) === null) return acc;

        if (field.prepopulateFromFields) {
          const prepopulated = resolvePrepopulatedValue(field, enduserForPrepopulation);
          if (prepopulated !== undefined) {
            // @ts-expect-error Union type gets in the way here
            const answer: FormResponseValueAnswer = { type: field.type, value: prepopulated };
            acc[field.id] = {
              fieldId: field.id,
              fieldTitle: field.title,
              answer,
              computedValueKey: deriveComputedValueKey(field.intakeField, answer),
              ...(isLockedWhenPrepopulated(field, answer) ? { disabled: true } : {}),
            };
            return acc;
          }
        }

        const seeded = seedEmptyResponse(field);
        if (seeded) acc[field.id] = seeded;
        return acc;
      }, {});

      if (!resolved) return {};

      const swept = dropStaleChoiceSelections({
        responses: resolved,
        fieldsArr: fieldsArr ?? [],
        logicOptions: {
          form,
          dateOfBirth: enduserForPrepopulation?.dateOfBirth,
          gender: enduserForPrepopulation?.gender,
          state: enduserForPrepopulation?.state,
        },
      }).responses;

      const fieldsById = new Map((fieldsArr ?? []).map((f) => [f.id, f]));
      return Object.entries(swept).reduce<LocalTellescopeFormResponseMap>((acc, [fieldId, response]) => {
        acc[fieldId] = applyFieldResponseMetadata(response, fieldsById.get(fieldId));
        return acc;
      }, {});
    },
    [seedEmptyResponse]
  );
};
